import { useState, useCallback } from 'react';
import { calculatePace, calculateOverallPace, convertPace } from '../utils';
import { createSegmentObject, updateSegmentsByLap } from '../utils/segmentUtils';
import { formatPace } from '../utils/formatters';
import useLapSegmentData from './useLapSegmentData';
import useSegmentDisplay from './useSegmentDisplay';
import { Segment, SegmentsByLap } from '../types';

/**
 * Type for pace unit
 */
export type PaceUnit = 'mile' | 'km';

/**
 * Options for segment tracking hook
 */
interface SegmentTrackingOptions {
  /** Current segment time in milliseconds */
  segmentTime: number;
  /** Total workout time in milliseconds */
  mainTime: number;
  /** Whether the timer is running */
  isRunning: boolean;
  /** Current lap number */
  currentLapNumber?: number;
  /** External segments data organized by lap */
  externalSegmentsByLap?: SegmentsByLap;
  /** Function to reset segment timer */
  resetSegmentTimer?: () => void;
  /** Callback when segment is recorded */
  onSegmentRecorded?: (segment: Segment) => void;
  /** Unit for pace calculation */
  paceUnit?: PaceUnit;
}

/**
 * Return type for useSegmentTracking hook
 */
interface SegmentTrackingResult {
  /** Currently highlighted segment distance */
  currentSegmentDistance: number | null;
  /** Array of all pace segments */
  paceSegments: Segment[];
  /** Pace of the last recorded segment */
  lastSegmentPace: number | null;
  /** Overall workout pace */
  overallPace: number | null;
  /** Total distance in meters */
  totalDistance: number;
  /** Total segment time in milliseconds */
  totalSegmentTime: number;
  /** Current lap distance in meters */
  currentLapDistance: number;
  /** Current lap time in milliseconds */
  currentLapTime: number;
  /** Current lap pace */
  currentLapPace: number | null;
  /** Pace of the last completed lap */
  lastCompletedLapPace: number | null;
  /** Segments organized by lap */
  segmentsByLap: SegmentsByLap;
  /** Effective segments by lap (either external or internal) */
  effectiveSegmentsByLap: SegmentsByLap;
  /** Whether to show all laps */
  showAllLaps: boolean;
  /** Record a segment with the given distance */
  recordSegment: (distanceMeters: number) => { segment: Segment | null; lapPace: number | null };
  /** Internal record segment function with callbacks */
  recordSegmentInternal: (
    distanceMeters: number,
    segmentRecordedCallback?: (segment: Segment) => void,
    segmentTimerResetCallback?: () => void
  ) => { segment: Segment | null; lapPace: number | null };
  /** Calculate pace for given time and distance */
  calculatePace: (time: number, distance: number) => number;
  /** Calculate overall pace for given time and distance */
  calculateOverallPace: (time: number, distance: number) => number;
  /** Format pace value as string */
  formatPace: (pace: number | null) => string;
  /** Get segments to display based on current view mode */
  getSegmentsToDisplay: () => Segment[];
  /** Toggle between showing all laps or just current lap */
  toggleLapView: () => void;
  /** Reset lap-specific segment data */
  resetLapSegmentData: (preserveLastSegmentPace?: boolean) => void;
  /** Reset lap data but preserve the last segment pace */
  resetLapDataPreservingPace: (completedLapPace?: number | null) => void;
  /** Set the last segment pace directly */
  setLastSegmentPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Set the last completed lap pace directly */
  setLastCompletedLapPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Get segment data for pace calculations */
  getSegmentDataForPaceCalculations: () => SegmentDataForPaceCalculations;
  /** Convert pace values when unit changes */
  convertPaceValues: (fromUnit: PaceUnit, toUnit: PaceUnit) => void;
}

/**
 * Segment data returned for pace calculations
 */
interface SegmentDataForPaceCalculations {
  /** Array of all pace segments */
  paceSegments: Segment[];
  /** Segments organized by lap */
  segmentsByLap: SegmentsByLap;
  /** Pace of the last recorded segment */
  lastSegmentPace: number | null;
  /** Current lap pace */
  currentLapPace: number | null;
  /** Pace of the last completed lap */
  lastCompletedLapPace: number | null;
  /** Total distance in meters */
  totalDistance: number;
  /** Total segment time in milliseconds */
  totalSegmentTime: number;
  /** Current lap distance in meters */
  currentLapDistance: number;
  /** Current lap time in milliseconds */
  currentLapTime: number;
}

/**
 * Custom hook for handling segment tracking logic
 *
 * This hook encapsulates the segment tracking functionality that was previously
 * in the Stopwatch component. It manages segment data recording, calculations,
 * and state updates without changing functionality.
 */
const useSegmentTracking = ({
  segmentTime,
  mainTime,
  isRunning,
  currentLapNumber = 1,
  externalSegmentsByLap,
  resetSegmentTimer,
  onSegmentRecorded,
  paceUnit = 'mile', // Accept paceUnit as a parameter instead of using context
}: SegmentTrackingOptions): SegmentTrackingResult => {
  // State for overall segment tracking
  const [paceSegments, setPaceSegments] = useState<Segment[]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [totalSegmentTime, setTotalSegmentTime] = useState<number>(0);
  const [overallPace, setOverallPace] = useState<number | null>(null);

  // Store the segment data grouped by lap number
  const [segmentsByLap, setSegmentsByLap] = useState<SegmentsByLap>({});

  // Use lap segment data hook for lap-specific tracking
  const {
    currentLapDistance,
    currentLapTime,
    currentLapPace,
    lastSegmentPace,
    lastCompletedLapPace,
    updateLapData,
    resetLapSegmentData,
    resetLapDataPreservingPace,
    setLastSegmentPace,
    setLastCompletedLapPace,
  } = useLapSegmentData();

  // Use segment display hook for UI-related functionality
  const {
    showAllLaps,
    currentSegmentDistance,
    effectiveSegmentsByLap,
    toggleLapView,
    highlightSegmentDistance,
    getSegmentsToDisplay,
  } = useSegmentDisplay({
    segments: paceSegments,
    segmentsByLap,
    externalSegmentsByLap,
    currentLapNumber,
  });

  /**
   * Record a segment with the given distance
   *
   * @param distanceMeters - Distance in meters
   * @param segmentRecordedCallback - Callback when segment is recorded
   * @param segmentTimerResetCallback - Function to reset the segment timer
   * @returns An object containing the recorded segment data and the calculated lap pace
   */
  const recordSegment = useCallback(
    (
      distanceMeters: number,
      segmentRecordedCallback?: (segment: Segment) => void,
      segmentTimerResetCallback?: () => void
    ): { segment: Segment | null; lapPace: number | null } => {
      if (!isRunning) return { segment: null, lapPace: null };

      // Validate distance
      if (typeof distanceMeters !== 'number' || !isFinite(distanceMeters) || distanceMeters <= 0) {
        console.warn('Invalid segment distance:', distanceMeters);
        return { segment: null, lapPace: null };
      }

      // Ensure we have a valid segment time
      if (segmentTime <= 0) {
        console.warn('Invalid segment time:', segmentTime);
        return { segment: null, lapPace: null };
      }

      // Create a unique segment number based on existing segments
      const segmentNumber = paceSegments.length + 1;
      const currentSegmentTime = segmentTime;

      // Calculate segment pace
      const segmentPace = calculatePace(currentSegmentTime, distanceMeters, paceUnit);

      // Update total distance and segment time
      const newTotalDistance = totalDistance + distanceMeters;
      const newTotalSegmentTime = totalSegmentTime + currentSegmentTime;

      setTotalDistance(newTotalDistance);
      setTotalSegmentTime(newTotalSegmentTime);

      // Calculate overall pace using total segment time and total distance
      const overallPaceValue = calculateOverallPace(
        newTotalSegmentTime,
        newTotalDistance,
        paceUnit
      );
      setOverallPace(overallPaceValue);

      // Create the segment data with lap association
      const newSegment = createSegmentObject({
        segmentNumber,
        lapNumber: currentLapNumber || 1,
        distanceMeters,
        currentSegmentTime,
        totalSegmentTime: newTotalSegmentTime,
        segmentPace,
      });

      // Record the current segment
      setPaceSegments(prevSegments => [...prevSegments, newSegment]);

      // Update the segments by lap structure (only if not using external segments)
      if (!externalSegmentsByLap) {
        setSegmentsByLap(prevSegmentsByLap => updateSegmentsByLap(prevSegmentsByLap, newSegment));
      }

      // Update current lap data AND capture the returned lap pace
      const calculatedLapPace = updateLapData({
        distanceMeters,
        segmentTime: currentSegmentTime,
        segmentPace: isFinite(segmentPace) ? segmentPace : null,
        paceUnit,
      });

      // Temporarily highlight the button
      highlightSegmentDistance(distanceMeters);

      // Notify parent component about the new segment
      if (segmentRecordedCallback) {
        segmentRecordedCallback(newSegment);
      }

      // Reset the segment timer if the callback was provided
      if (segmentTimerResetCallback) {
        segmentTimerResetCallback();
      }

      // Return both segment and lap pace
      return { segment: newSegment, lapPace: calculatedLapPace };
    },
    [
      isRunning,
      paceSegments,
      segmentTime,
      totalDistance,
      totalSegmentTime,
      currentLapNumber,
      externalSegmentsByLap,
      updateLapData,
      highlightSegmentDistance,
      paceUnit,
    ]
  );

  /**
   * Wrapper for recordSegment that uses the props passed to the hook
   * @param distanceMeters - Distance in meters for the segment
   */
  const recordSegmentWithProps = useCallback(
    (distanceMeters: number): { segment: Segment | null; lapPace: number | null } => {
      if (isRunning) {
        const result = recordSegment(distanceMeters, onSegmentRecorded, resetSegmentTimer);
        return result;
      }
      return { segment: null, lapPace: null };
    },
    [isRunning, recordSegment, onSegmentRecorded, resetSegmentTimer]
  );

  /**
   * Get the current segment data for pace calculations
   * This can be used to preserve pace data when a workout is completed
   */
  const getSegmentDataForPaceCalculations = useCallback((): SegmentDataForPaceCalculations => {
    return {
      paceSegments,
      segmentsByLap,
      lastSegmentPace,
      currentLapPace,
      lastCompletedLapPace,
      totalDistance,
      totalSegmentTime,
      currentLapDistance,
      currentLapTime,
    };
  }, [
    paceSegments,
    segmentsByLap,
    lastSegmentPace,
    currentLapPace,
    lastCompletedLapPace,
    totalDistance,
    totalSegmentTime,
    currentLapDistance,
    currentLapTime,
  ]);

  /**
   * Convert pace values when unit changes
   * @param fromUnit - Source unit ('mile' or 'km')
   * @param toUnit - Target unit ('mile' or 'km')
   */
  const convertPaceValues = useCallback(
    (fromUnit: PaceUnit, toUnit: PaceUnit): void => {
      // Skip if units are the same
      if (fromUnit === toUnit) return;

      // Convert lastSegmentPace if it exists and is valid
      if (lastSegmentPace !== null && isFinite(lastSegmentPace)) {
        const convertedSegmentPace = convertPace(lastSegmentPace, fromUnit, toUnit);
        if (convertedSegmentPace !== null) {
          setLastSegmentPace(convertedSegmentPace);
        }
      }

      // Convert lastCompletedLapPace if it exists and is valid
      if (lastCompletedLapPace !== null && isFinite(lastCompletedLapPace)) {
        const convertedLastCompletedLapPace = convertPace(lastCompletedLapPace, fromUnit, toUnit);
        if (convertedLastCompletedLapPace !== null) {
          setLastCompletedLapPace(convertedLastCompletedLapPace);
        }
      }

      // When we have current lap data, we can recalculate the lap pace explicitly
      if (currentLapDistance > 0 && currentLapTime > 0) {
        // Recalculate the pace using the new unit
        const newLapPace = calculatePace(currentLapTime, currentLapDistance, toUnit);

        // Only update if calculation produced a valid result
        if (newLapPace !== null && isFinite(newLapPace)) {
          // Use the enhanced updateLapData to set the new pace
          updateLapData({
            distanceMeters: 0,
            segmentTime: 0,
            segmentPace: null,
            paceUnit: toUnit,
            forceRecalculateLapPace: true,
            newLapPace: newLapPace,
          });
        }
      } else if (currentLapPace !== null && isFinite(currentLapPace)) {
        // If we don't have raw data but do have a current lap pace, convert it directly
        const convertedLapPace = convertPace(currentLapPace, fromUnit, toUnit);
        if (convertedLapPace !== null && isFinite(convertedLapPace)) {
          updateLapData({
            distanceMeters: 0,
            segmentTime: 0,
            segmentPace: null,
            paceUnit: toUnit,
            forceRecalculateLapPace: true,
            newLapPace: convertedLapPace,
          });
        }
      }

      // Convert overall pace
      if (overallPace !== null && isFinite(overallPace)) {
        const convertedOverallPace = convertPace(overallPace, fromUnit, toUnit);
        if (convertedOverallPace !== null) {
          setOverallPace(convertedOverallPace);
        }
      }

      // Convert paceSegments array
      if (paceSegments.length > 0) {
        const newPaceSegments = paceSegments.map(segment => {
          if (segment.pace !== null && isFinite(segment.pace)) {
            const convertedPace = convertPace(segment.pace, fromUnit, toUnit);
            return {
              ...segment,
              pace: convertedPace,
            };
          }
          return segment;
        });
        setPaceSegments(newPaceSegments);
      }

      // Convert segmentsByLap object
      if (Object.keys(segmentsByLap).length > 0) {
        const newSegmentsByLap: SegmentsByLap = {};

        for (const lapNum in segmentsByLap) {
          const segments = segmentsByLap[lapNum];
          if (segments && segments.length > 0) {
            newSegmentsByLap[lapNum] = segments.map(segment => {
              if (segment.pace !== null && isFinite(segment.pace)) {
                const convertedPace = convertPace(segment.pace, fromUnit, toUnit);
                return {
                  ...segment,
                  pace: convertedPace,
                };
              }
              return segment;
            });
          } else {
            newSegmentsByLap[lapNum] = [];
          }
        }

        setSegmentsByLap(newSegmentsByLap);
      }
    },
    [
      lastSegmentPace,
      lastCompletedLapPace,
      currentLapPace,
      currentLapDistance,
      currentLapTime,
      overallPace,
      paceSegments,
      segmentsByLap,
      setLastSegmentPace,
      setLastCompletedLapPace,
      setOverallPace,
      updateLapData,
      setPaceSegments,
      setSegmentsByLap,
    ]
  );

  return {
    // State
    currentSegmentDistance,
    paceSegments,
    lastSegmentPace,
    overallPace,
    totalDistance,
    totalSegmentTime,
    currentLapDistance,
    currentLapTime,
    currentLapPace,
    lastCompletedLapPace,
    segmentsByLap,
    effectiveSegmentsByLap,
    showAllLaps,

    // Functions
    recordSegment: recordSegmentWithProps, // Export the wrapper function
    recordSegmentInternal: recordSegment, // For tests and advanced usage
    calculatePace: (time: number, distance: number) => calculatePace(time, distance, paceUnit),
    calculateOverallPace: (time: number, distance: number) =>
      calculateOverallPace(time, distance, paceUnit),
    formatPace: (pace: number | null) => formatPace(pace, paceUnit),
    getSegmentsToDisplay,
    toggleLapView,
    resetLapSegmentData,
    resetLapDataPreservingPace,
    setLastSegmentPace,
    setLastCompletedLapPace,
    getSegmentDataForPaceCalculations,
    convertPaceValues,
  };
};

export default useSegmentTracking;
